import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useTripListPricingContext } from './ChatTripPlannerPage/contexts/TripListPricingContext'
import { useCartButtonSummary } from './ChatTripPlannerPage/hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from './ChatTripPlannerPage/hooks/useFetchCartPanelOffers'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import CartButton from './ChatTripPlannerPage/components/CartButton'
import DownloadItineraryButton from './ChatTripPlannerPage/components/DownloadItineraryButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineCommentEditIcon from 'LuxKit/Icons/line/LineCommentEditIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import Group from 'components/utils/Group'
import ContactOptions from 'components/Common/ContactOptions'
import {
  TripPlannerStickyHeader,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import Clickable from 'components/Common/Clickable'
import NavBarRegionButton from 'components/NavKit/NavBarButtons/NavBarRegionButton'
import { useTripPlannerMenuDrawer } from './ChatTripPlannerPage/hooks/useTripPlannerMenuDrawer'

const TertiaryHeader = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(4)} ${rem(16)};
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const StyledStickyHeader = styled(TripPlannerStickyHeader)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const HeaderNavBarRegionButton = styled(NavBarRegionButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

const HeaderDownloadItineraryButton = styled(DownloadItineraryButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  chatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  isRewindMode: boolean
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  className?: string
}

function TripPlannerHeader({
  chatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  isRewindMode,
  onDevModeToggle,
  onRewindModeToggle,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const eventBus = useTripPlannerEventBus()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  const { luxPlusEnabled } = useTripListPricingContext()
  useFetchCartPanelOffers(itineraryData, true)
  const cartSummary = useCartButtonSummary(itineraryData, isLoggedIn, luxPlusEnabled)

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const handleOpenDrawer = useTripPlannerMenuDrawer({
    isLoggedIn,
    isStaff,
    isDevMode,
    isRewindMode,
    onStartNewChat,
    onChatSelect,
    onChatDeleted,
    onDevModeToggle,
    onRewindModeToggle,
  })

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const backButtonText = intl.formatMessage({
    id: 'uvkQRhGsEtSZkBcb2NHS',
    defaultMessage: 'Back to Luxury Escapes',
    description: 'Navigation button text to return to the main Luxury Escapes site from TripPlanner',
  })

  const giveFeedbackText = intl.formatMessage({
    id: 'DwyX2RGwDgu5GbANxFYx',
    defaultMessage: 'Give feedback',
    description: 'Button text in the TripPlanner header to open the feedback survey for the feature',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const betaLabel = intl.formatMessage({
    id: 'gO283xrN3xwQSsK25Jy9',
    defaultMessage: 'Beta',
    description: 'Beta badge label for AI TripPlanner in the header in the trip planner AI chat',
  })

  const showCartButton = !!itineraryData

  const rightContent = useMemo(() => {
    if (isLoggedIn) {
      return (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {hasItineraryData && <HeaderDownloadItineraryButton chatId={chatId} />}
          <HeaderNavBarRegionButton />
          {showCartButton && <CartButton
            itemCount={cartSummary.bookableItemCount}
            totalPrice={cartSummary.estimatedTotal}
            isLoading={cartSummary.isLoading}
            onClick={handleCartClick}
          />}
        </Group>
      )
    }

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasItineraryData && <HeaderDownloadItineraryButton chatId={chatId} />}
        <HeaderNavBarRegionButton />
        {showCartButton && <CartButton
          itemCount={cartSummary.bookableItemCount}
          totalPrice={cartSummary.estimatedTotal}
          isLoading={cartSummary.isLoading}
          onClick={handleCartClick}
        />}
        <TextButton
          kind="secondary"
          size="medium"
          onClick={handleLogin}
        >
          {loginButtonText}
        </TextButton>
      </Group>
    )
  }, [isLoggedIn, hasItineraryData, chatId, handleLogin, loginButtonText, showCartButton, cartSummary.bookableItemCount, cartSummary.estimatedTotal, cartSummary.isLoading, handleCartClick])

  return (
    <Group direction="vertical" className={className}>
      <TertiaryHeader
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        fullWidth
      >
        <TextButton
          kind="tertiary"
          size="small"
          startIcon={<LineArrowLeftIcon />}
          to="/"
        >
          {backButtonText}
        </TextButton>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          <ContactOptions />
          <TextButton
            kind="tertiary"
            size="small"
            startIcon={<LineCommentEditIcon />}
            href="https://usabi.li/do/gizugyzacokd/vj9hxb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {giveFeedbackText}
          </TextButton>
        </Group>
      </TertiaryHeader>
      <StyledStickyHeader>
        <LeftContent>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <IconButton
              kind="tertiary"
              size="medium"
              onClick={handleOpenDrawer}
              aria-label={openMenuAriaLabel}
            >
              <LineBarsIcon />
            </IconButton>
            <Clickable to="/trip-planner/chat-trip-planner/new">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LogoWrapper>
                  <TripPlannerStyledLogo />
                </LogoWrapper>
                <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
                <Label kind="primary" variant="highlight">{betaLabel}</Label>
              </Group>
            </Clickable>
          </Group>
        </LeftContent>
        {rightContent && (
          <RightContent>
            {rightContent}
          </RightContent>
        )}
      </StyledStickyHeader>
    </Group>
  )
}

export default TripPlannerHeader
